import { z } from 'zod';
import { CorpusStatusSchema } from './enums.js';
import { PolicyAcceptanceMethodSchema } from './policy-acceptance.js';

// ---------------------------------------------------------------------------
// Document register: every controlled document (corpus item) of an
// organisation against the standard clauses and Annex A controls it supports,
// with the signature position of each policy published for acceptance and of
// each member of staff. Read-only view over corpus_items, crosswalk_mappings,
// clauses.corpus_keys and the policy acceptance tables; routes in
// apps/worker/src/routes/document-register.ts.
// ---------------------------------------------------------------------------

/**
 * A clause reference. `annex` when the clause number is an ISO 27001 Annex A
 * control (`A.n…`); `standard` for a crosswalk mapping to a whole standard or
 * version (no clause) — then `clauseId` and `clauseNumber` are null.
 */
export const DocumentClauseKindSchema = z.enum(['clause', 'annex', 'standard']);
export type DocumentClauseKind = z.infer<typeof DocumentClauseKindSchema>;

/**
 * Where the applicability came from: the standard's own clause → corpus key
 * suggestions (`standard`), the organisation's crosswalk (`crosswalk`), or both.
 */
export const DocumentClauseSourceSchema = z.enum(['standard', 'crosswalk', 'both']);
export type DocumentClauseSource = z.infer<typeof DocumentClauseSourceSchema>;

export const DocumentClauseRefSchema = z.object({
  standardVersionId: z.string(),
  standardCode: z.string(),
  /** The version label shown to people, e.g. "ISO 27001:2022". */
  standardLabel: z.string(),
  clauseId: z.string().nullable(),
  clauseNumber: z.string().nullable(),
  title: z.string(),
  kind: DocumentClauseKindSchema,
  source: DocumentClauseSourceSchema,
});
export type DocumentClauseRef = z.infer<typeof DocumentClauseRefSchema>;

/** Signed / not signed counts over the in-scope members of one policy. */
export const DocumentSignatureCountsSchema = z.object({
  signed: z.number().int(),
  notSigned: z.number().int(),
  /** Subset of `notSigned` whose due date has passed. */
  overdue: z.number().int(),
  total: z.number().int(),
  /** Whole percent of `signed / total`; 0 when `total` is 0. */
  percent: z.number().int(),
});
export type DocumentSignatureCounts = z.infer<typeof DocumentSignatureCountsSchema>;

export const DocumentRegisterReleaseSchema = z.object({
  releaseId: z.string(),
  versionNumber: z.number().int(),
  /** The released revision's own document version label ('' when it names none). */
  documentVersion: z.string(),
  publishedAt: z.string(),
  publishedByName: z.string().nullable(),
  dueDays: z.number().int(),
  reacceptIntervalMonths: z.number().int().nullable(),
});
export type DocumentRegisterRelease = z.infer<typeof DocumentRegisterReleaseSchema>;

export const DocumentRegisterEntrySchema = z.object({
  corpusItemId: z.string(),
  key: z.string(),
  title: z.string(),
  description: z.string(),
  status: CorpusStatusSchema,
  currentVersion: z.number().int(),
  /** The document's own version label ("3.1.0"), '' when it names none — see corpus.ts. */
  documentVersion: z.string(),
  updatedAt: z.string(),
  updatedByName: z.string().nullable(),
  /** Sorted by standard label, then clauses before Annex A controls, then number. */
  clauses: z.array(DocumentClauseRefSchema),
  /** The live policy release, null when the document is not published for acceptance. */
  release: DocumentRegisterReleaseSchema.nullable(),
  /** Null when the document is not published for acceptance. */
  signatures: DocumentSignatureCountsSchema.nullable(),
});
export type DocumentRegisterEntry = z.infer<typeof DocumentRegisterEntrySchema>;

export const DocumentRegisterStaffSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  joinedAt: z.string(),
  signed: z.number().int(),
  notSigned: z.number().int(),
  overdue: z.number().int(),
  /** Number of policies published for acceptance. */
  total: z.number().int(),
  /** Whole percent of `signed / total`; 0 when nothing is published. */
  percent: z.number().int(),
});
export type DocumentRegisterStaff = z.infer<typeof DocumentRegisterStaffSchema>;

export const DocumentRegisterStandardSchema = z.object({
  versionId: z.string(),
  standardId: z.string(),
  code: z.string(),
  name: z.string(),
  label: z.string(),
});
export type DocumentRegisterStandard = z.infer<typeof DocumentRegisterStandardSchema>;

export const DocumentRegisterSummarySchema = z.object({
  /** All documents, archived included. */
  documents: z.number().int(),
  /** Documents with at least one clause reference. */
  mapped: z.number().int(),
  /** Documents published for acceptance (one live release each). */
  policies: z.number().int(),
  /** Published policies every in-scope member has signed. */
  policiesFullySigned: z.number().int(),
  policiesNotFullySigned: z.number().int(),
  /** Member × policy signatures across the whole programme. */
  signatures: DocumentSignatureCountsSchema,
  staff: z.object({
    total: z.number().int(),
    /** Members who have signed every published policy. */
    fullySigned: z.number().int(),
    /** Whole percent of `fullySigned / total`; 0 when no members or no policies. */
    percentFullySigned: z.number().int(),
  }),
});
export type DocumentRegisterSummary = z.infer<typeof DocumentRegisterSummarySchema>;

export const DocumentRegisterSchema = z.object({
  generatedAt: z.string(),
  /** Standard versions the clause references were resolved against. */
  standards: z.array(DocumentRegisterStandardSchema),
  documents: z.array(DocumentRegisterEntrySchema),
  staff: z.array(DocumentRegisterStaffSchema),
  summary: DocumentRegisterSummarySchema,
});
export type DocumentRegister = z.infer<typeof DocumentRegisterSchema>;

/** One row of the signature log (every acceptance ever, former members included). */
export const DocumentSignatureLogEntrySchema = z.object({
  acceptanceId: z.string(),
  corpusItemId: z.string(),
  title: z.string(),
  versionNumber: z.number().int(),
  documentVersion: z.string(),
  userName: z.string(),
  userEmail: z.string(),
  signedAt: z.string(),
  method: PolicyAcceptanceMethodSchema,
  recordedByName: z.string().nullable(),
  source: z.string(),
  contentSha256: z.string(),
  statement: z.string(),
});
export type DocumentSignatureLogEntry = z.infer<typeof DocumentSignatureLogEntrySchema>;

/** `true` for an ISO 27001 Annex A control number such as "A.5.10". */
export function isAnnexControlNumber(clauseNumber: string): boolean {
  return /^A\.\d/i.test(clauseNumber.trim());
}
